import { Columns } from '@/types';
import { Close } from '@mui/icons-material';
import Grid from '@mui/material/Grid2';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { DialogActionButton } from '../../shared/styles';
import { Footer, Header, TitleText } from '../styles';

type ColumnSelectionDialogProps = {
  allHeaders: Columns[];
  isOpen: boolean;
  onClose: () => void;
  handleColumnSelection: (ids: (string | undefined)[]) => void;
};

export const ColumnSelectionDialog = ({
  allHeaders,
  isOpen,
  onClose,
  handleColumnSelection,
}: ColumnSelectionDialogProps) => {
  const [updatedHeaders, setUpdatedHeaders] = useState<Columns[]>([]);

  useEffect(() => {
    setUpdatedHeaders(allHeaders);
  }, [allHeaders]);

  const selectedColumns = useMemo(() => {
    return updatedHeaders.map((header) => header.templateFieldKey);
  }, [updatedHeaders]);

  const updateColumnSelection = useCallback(
    (key: string | undefined) => {
      if (!key) return;
      if (selectedColumns.includes(key)) {
        setUpdatedHeaders(updatedHeaders?.filter((header) => header.templateFieldKey !== key));
      } else {
        const newHeader = allHeaders.find((header) => header.templateFieldKey === key);
        if (newHeader) {
          setUpdatedHeaders((previousUpdatedHeaders) => [
            ...(previousUpdatedHeaders || []),
            newHeader,
          ]);
        }
      }
    },
    [allHeaders, selectedColumns, updatedHeaders]
  );

  const handleSubmit = useCallback(() => {
    const allIds = updatedHeaders?.map((updatedHeader) => updatedHeader.templateFieldKey);
    handleColumnSelection(allIds);
    onClose();
  }, [handleColumnSelection, onClose, updatedHeaders]);

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      PaperProps={{
        sx: {
          maxWidth: '82rem',
        },
      }}
    >
      <Header>
        <TitleText>Customize Table Columns</TitleText>
        <IconButton onClick={handleCancel} sx={{ padding: 0 }} disableRipple>
          <Close sx={{ fontSize: '2.4rem', color: 'rgba(0,0,0,0.54)' }} />
        </IconButton>
      </Header>
      <DialogContent sx={{ paddingLeft: '4.4rem' }}>
        <Grid container paddingBottom="4rem" rowSpacing="2.4rem" width="100%">
          {allHeaders
            .filter((header) => header.columnName !== 'View')
            .map((column) => (
              <Grid size={4} key={column.key}>
                <FormControlLabel
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      fontSize: '1.4rem',
                    },
                  }}
                  control={
                    <Checkbox
                      size="small"
                      checked={selectedColumns.includes(column.templateFieldKey)}
                      onChange={() => updateColumnSelection(column.templateFieldKey)}
                    />
                  }
                  label={column.columnName}
                />
              </Grid>
            ))}
        </Grid>
      </DialogContent>
      <Footer>
        <DialogActionButton onClick={handleCancel}>Cancel</DialogActionButton>
        <DialogActionButton onClick={handleSubmit} contained>
          Confirm
        </DialogActionButton>
      </Footer>
    </Dialog>
  );
};
